import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { primaryNav, primaryCta, signInCta } from "@/config/site";
import "./MobileNav.css";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function MobileNav({ open, onClose, triggerRef }: MobileNavProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const panel = panelRef.current;
    const focusables = panel?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    focusables?.[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      const nodes = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    const trigger = triggerRef.current;

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      trigger?.focus();
    };
  }, [open, onClose, triggerRef]);

  if (!open) return null;

  return (
    <div className="mobile-nav-overlay">
      <div
        className="mobile-nav-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        ref={panelRef}
      >
        <div className="mobile-nav-header">
          <span className="mobile-nav-title">Menu</span>
          <button type="button" className="mobile-nav-close" onClick={onClose}>
            <span aria-hidden="true">&times;</span>
            <span className="visually-hidden">Close menu</span>
          </button>
        </div>
        <nav aria-label="Primary">
          <ul className="mobile-nav-list">
            {primaryNav.map((item) => (
              <li key={item.path}>
                <NavLink to={item.path} onClick={onClose}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="mobile-nav-footer">
          <a href={signInCta.path} onClick={onClose} className="mobile-nav-signin">
            {signInCta.label}
          </a>
          <NavLink to={primaryCta.path} onClick={onClose} className="mobile-nav-cta">
            {primaryCta.label}
          </NavLink>
        </div>
      </div>
      <button
        type="button"
        className="mobile-nav-scrim"
        aria-label="Close menu"
        onClick={onClose}
        tabIndex={-1}
      />
    </div>
  );
}
